import type { StrategyNode } from '../types/BaseNode';
import type { DataTypeSpec } from '../hooks/useDataTypes';
import { cloneNode } from './handleDrop';

/**
 * Traverses a strategy node tree to find the "primary" underlying price data source.
 * This is used for auto-suggesting an exit basis in Hold Mode.
 */
export function findUnderlyingPriceNode(
  node: StrategyNode | undefined,
  dataTypes: DataTypeSpec[]
): StrategyNode | null {
  if (!node) return null;

  // 1. If it's a Single node, check if it's a "price" nature
  if (node.type === 'Single') {
    const config = (node as any).config ?? {};
    const typeId = config.typeId ?? config.type_id;
    const dataType = dataTypes.find(dt => dt.id === typeId);

    if (dataType?.data_nature === 'price') {
      return cloneNode(node);
    }
    // Fallback: pick the first Single node if it's the only one
    return cloneNode(node);
  }

  // 2. If it's a Formatter, keep digging into the child
  if (node.type === 'Formatter') {
    const child = (node as any).config?.child;
    return findUnderlyingPriceNode(child, dataTypes);
  }

  // 3. If it's a Composite (Logic/Math), follow the "target" or "primary" leg.
  // In MoneyMaker, the 'left' leg is usually the primary data source being filtered or compared.
  if (node.type === 'Composite') {
    const left = (node as any).config?.left;
    return findUnderlyingPriceNode(left, dataTypes);
  }

  return null;
}
